import { Building, Neighborhood } from 'apolloClient/types';
import { City } from 'apolloClient/types/City';
import { County } from 'apolloClient/types/County';

// groups of the search input, named as the backend searchInputItems query names them
export type SearchInputCategory =
  | 'counties'
  | 'cities'
  | 'neighborhoods'
  | 'buildings'
  | 'preconstruction'
  | 'addresses'
  | 'zipCodes'
  | 'unitUniques';

export type SearchInputItem = {
  // a GraphQL ID, so a string at runtime — typed as number like every other
  // Strapi id in this app, and passed through untouched so the strict
  // comparisons in handleFiltersChange keep matching
  id?: number | null;
  label: string;
  slug?: string | null;
  link?: string | null;
};

export type SearchInputGroup = {
  category: SearchInputCategory;
  items: SearchInputItem[];
  page: number;
  pageCount: number;
};

export type SearchInputItemsResponse = {
  searchInputItems: SearchInputGroup[];
};

// already chosen filters, excluded from the suggestions
export type SearchInputSelected = {
  countyIds: Array<number | string>;
  cityIds: Array<number | string>;
  neighborhoodIds: Array<number | string>;
  buildingIds: Array<number | string>;
  zipCodes: string[];
};

export type SearchInputItemsVariables = {
  text: string;
  categories?: SearchInputCategory[];
  page?: number;
  selected?: SearchInputSelected;
};

// cities and counties never offer "load more" in the input
export type PaginableFields = Exclude<
  SearchInputCategory,
  'cities' | 'counties'
>;

export enum FilterRootFieldNameMap {
  city = 'cities',
  county = 'counties',
  neighborhood = 'neighborhoods',
  building = 'buildings',
  address = 'addresses',
}

export type QueriesKeys =
  | 'counties'
  | 'cities'
  | 'neighborhoods'
  | 'buildings'
  | 'addresses'
  | 'zipCodes';

export type AutocompleteCategories =
  | QueriesKeys
  | 'preconstruction'
  | 'recent'
  | 'unitUniques';

export type SearchItem = {
  id?: number;
  label: string;
  category: AutocompleteCategories;
  link?: string;
  slug?: string;
};

export type UpdateValue = {
  id?: number;
  slug?: string;
  name: string;
  city?: number;
  county?: number;
  neighborhood?: number;
};

export type UpdateHandler = (
  value: UpdateValue,
  filters: SearchBoxFilters,
  openConfirmDialog?: (closeHandler: (value: boolean) => void) => void
) => Promise<SearchBoxFilters>;

export type RootField =
  | 'city'
  | 'county'
  | 'neighborhood'
  | 'building'
  | 'address';

export type BuildingsFilter = Pick<Building, 'slug' | 'id' | 'name'> & {
  isPreconstruction?: boolean;
};

export type NeighborhoodsFilter = Pick<Neighborhood, 'id' | 'slug' | 'name'>;

export type CitiesFilter = City;
export type CountiesFilter = County;

export type SearchBoxFilters = {
  counties: CountiesFilter[];
  cities: CitiesFilter[];
  zipCodes: string[];
  neighborhoods: NeighborhoodsFilter[];
  buildings: BuildingsFilter[];
  addresses: BuildingsFilter[];
};

export const initialSearchBoxFilters: SearchBoxFilters = {
  cities: [],
  counties: [],
  zipCodes: [],
  neighborhoods: [],
  buildings: [],
  addresses: [],
};

export type DataForAutocompleteInterface = {
  [key in AutocompleteCategories]: string[];
};
